import { IPageConfiguration } from '../../domain/conf/page'
import { IEvent } from '../../domain/event/event'
import { DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { IProfile } from '../common/profile'
import { IProviderConfig } from '../common/provider-config'
import { IAnalyticsAdapter } from './adapters/adapter'
import { IAnalyticsMetaFormatter } from './formatters/default'

export interface IAnalyticsServiceConfiguration {
    // ad
    adSite?: string
    // analytics
    provider: IProviderConfig
    brand?: string
    domain?: string
    division?: string
    vendor?: string
    debug?: string
    // identity
    profile: IProfile
    // page
    page: IPageConfiguration
}

export class AnalyticsService extends SimpleEvents {
    public static ACTION_PAGE_LOAD = 'page-load'

    public static DOM = {
        attrs: {
            action: 'data-evt',
            data: 'data-evt-val',
            type: 'data-evt-typ'
        }
    }

    /**
     * Define all page load event types that can be handled
     */
    public static pageLoadEventTypes = {
        accountConfirmed: 'account-confirmed',
        campaign: 'campaign',
        creditCardInfo: 'crdit-card-info',
        embeddedVideoLoadStory: 'embedded-video-load-story',
        errorMessageDisplayed: 'error-message-displayed',
        newReg: 'new-registration',
        outfitVideoPlayerLoad: 'outfit-video-player-load',
        regThankScreen: 'reg-thanks-screen',
        search: 'search',
        signin: 'signin',
        verificationScreen: 'verification-screen',
        videoCenter: 'video-center',
        viewArticle: 'view-article',
        viewCategory: 'view-category',
        viewPage: 'page-view',
        widgetPlaylistLoad: 'widget-playlist-load',
        widgetPlaylistPlayer: 'widget-playlist-player',
        widgetVideoLoad: 'widget-video-load',
    }

    /**
     * Define all click event types that can be handled
     */
    public static clickEventTypes = {
        accountModal: 'account-modal',
        button: 'button',
        consent: 'consent',
        internalPromotionClick: 'internal-promotion-click',
        navigation: 'navigation',
        photoGallery: 'photo-gallery',
        signinRegModule:'signin-reg-module',
        signout: 'signout',
        socialShare: 'social-share',
        subscribeNewsletter: 'subscribe-newsletter',
        subscriptionButton: 'subscription-button-click',
        videoFullScreen: 'video-full-screen',
        videoPause: 'video-pause',
        videoPlay: 'video-play',
        widget: 'widget'
    }

    public static scrollEventTypes = {
        percentPageview: 'percent-pageview',
        widgetInview: 'widget-inview'
    }

    public meta: object

    public constructor(
        public adapter: IAnalyticsAdapter<IProviderConfig>,
        public formatter: IAnalyticsMetaFormatter) {
        super()
    }

    public async initialize(conf: IAnalyticsServiceConfiguration): Promise<void> {

        // format meta
        const meta = this.formatter.meta(conf)
        this.meta = meta

        // initialize adapter
        this.adapter.initialize(conf.provider, conf.profile, await this.getDocEvents(), meta)
    }

    public onIdentityChange(profile: IProfile): void {
        this.adapter.identityChange(profile, this.meta)
    }

    private async getDocEvents(): Promise<IEvent[]> {
        await DOMUtils.ready
        return [...document.querySelectorAll(`[${AnalyticsService.DOM.attrs.action}]`)]
            .map((element: Element) => this.adapter.getEventByType(
                element.getAttribute(AnalyticsService.DOM.attrs.type) || 'event',
                element.getAttribute(AnalyticsService.DOM.attrs.data) || '',
                element.getAttribute(AnalyticsService.DOM.attrs.action) || 'click',
                element))
    }
}
